/* eslint-disable */
// ─── User-facing error messages ───────────────────────────────────────────────
// Database and PostgREST errors are written for developers. They leak schema
// details ("Could not find the 'class_type' column of 'classes'"), constraint
// names, and sometimes raw SQL — none of which a yoga school owner can act on,
// and all of which tells an attacker about the schema.
//
// friendlyError() maps a failure to something a person can act on, and logs the
// technical detail to the console so it is still there when debugging.
//
// Rule of thumb for the copy: say what happened, and what to do next. Never
// echo a column name, a constraint name, or SQL.

// Pull a message string out of whatever the various api helpers return.
function rawOf(payload) {
  if (!payload) return "";
  if (typeof payload === "string") {
    try { return JSON.parse(payload)?.message || payload; } catch { return payload; }
  }
  if (payload.message) {
    // SchoolDashboard's api.patch puts the whole response body in .message
    try { return JSON.parse(payload.message)?.message || payload.message; }
    catch { return payload.message; }
  }
  return "";
}

function codeOf(payload) {
  if (!payload || typeof payload === "string") return "";
  if (payload.code) return payload.code;
  try { return JSON.parse(payload.message)?.code || ""; } catch { return ""; }
}

const GENERIC = "Something went wrong saving this. Please try again.";

export function friendlyError(payload, { context = "save" } = {}) {
  const raw  = rawOf(payload);
  const code = codeOf(payload);
  const status = payload?.status;

  // Keep the developer detail reachable without showing it.
  if (raw || code) console.error(`[${context}]`, code || "", raw || payload);

  // Session / permission
  if (status === 401 || status === 403 || code === "42501" || /JWT|not authorized|permission denied/i.test(raw)) {
    return "You don't have permission to do that, or your session expired. Please sign in again.";
  }

  // The form is ahead of the database — a missing column. The user cannot fix
  // this and should not be shown the column name.
  if (code === "PGRST204" || /Could not find the .* column|column .* does not exist|schema cache/i.test(raw)) {
    return "We couldn't save this — part of this form isn't available on the server yet. Please contact support so we can fix it.";
  }

  // Uniqueness
  if (code === "23505" || /duplicate key|unique constraint/i.test(raw)) {
    return "That's already taken. Please choose something else.";
  }

  // Our own trigger messages are written for people. Recognise them by shape
  // and return app-owned copy rather than echoing the database.
  if (/is reserved/i.test(raw))        return "That address is reserved. Please choose another.";
  if (/already taken/i.test(raw))      return "That address is already taken. Please choose another.";
  if (/only be set once/i.test(raw) || /not verified/i.test(raw)) {
    return "Profile addresses are available once your listing is verified.";
  }

  // Format / range constraints
  if (code === "23514" || /check constraint|violates check/i.test(raw)) {
    return "Some of the details entered aren't valid. Please review the highlighted fields.";
  }
  if (code === "22P02" || /invalid input syntax/i.test(raw)) {
    return "Some of the details entered aren't in the right format. Please review and try again.";
  }

  // Missing related record
  if (code === "23503" || /foreign key/i.test(raw)) {
    return "Something this depends on is missing. Please refresh the page and try again.";
  }

  // Required field
  if (code === "23502" || /null value in column/i.test(raw)) {
    return "A required field is missing. Please complete the form and try again.";
  }

  if (/Failed to fetch|NetworkError|network/i.test(raw)) {
    return "Network problem — please check your connection and try again.";
  }

  return GENERIC;
}

// True when an api helper's return value represents a failure. The helpers
// disagree about shape, which is half the reason this module exists.
export function isApiError(result) {
  if (result === null || result === undefined) return true;
  if (Array.isArray(result)) return false;
  return !!(result.error || result.code || result.message);
}
